import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { listOntologyTerms, type TermCard } from "../../../api/content/ontologyApi";
import { useProjects } from "../../../projects/projectContext";
import { iriLocalName } from "../../../shared/references/shortReferences";
import { orderedLabels } from "../viewer/ontologyModel";

/**
 * The data model's terms by IRI, as the term owner lists them at one model version (F94, F102
 * X-05): the one place the web app resolves a term's name. A model version is a commit, which
 * never changes, so its terms are read once a session and shared.
 */
export type TermCards = ReadonlyMap<string, TermCard>;

const read = new Map<string, Promise<TermCards>>();

function termCardsAt(projectId: string, commit: string): Promise<TermCards> {
  const key = `${projectId} ${commit}`;
  let cards = read.get(key);
  if (cards === undefined) {
    cards = listOntologyTerms(projectId, commit, null).then((listing) => new Map(listing.terms.map((card) => [card.iri, card])));
    // A failed read is tried again by the next page that asks.
    cards.catch(() => { read.delete(key); });
    read.set(key, cards);
  }
  return cards;
}

/** The chosen project's terms at the model version `commit`; `null` while they load, or without a version or project. */
export function useTermCards(commit: string | null): TermCards | null {
  return useProjectTermCards(useProjects().selectedProjectId, commit);
}

/**
 * The terms of `projectId` at its model version `commit`, for a view bound to a project of its
 * own (a run preparation's search keeps its project when the menu switches); `null` as for
 * `useTermCards`.
 */
export function useProjectTermCards(projectId: string | null, commit: string | null): TermCards | null {
  const [loaded, setLoaded] = useState<{ readonly key: string; readonly cards: TermCards } | null>(null);
  const key = projectId === null || commit === null ? null : `${projectId} ${commit}`;
  useEffect(() => {
    if (projectId === null || commit === null) return;
    let current = true;
    termCardsAt(projectId, commit).then(
      (cards) => { if (current) setLoaded({ key: `${projectId} ${commit}`, cards }); },
      () => { /* The term's own name stands in (the neutral fallback, F102 CP-02). */ },
    );
    return () => { current = false; };
  }, [projectId, commit]);
  return loaded !== null && loaded.key === key ? loaded.cards : null;
}

/**
 * The terms of `projectId` at each of its model versions `commits`, by commit, for a view that
 * shows statements written against several, bound to their project as `useProjectTermCards` is:
 * `null` for a version whose terms could not be read, as without a project (the neutral fallback);
 * one still loading is missing.
 */
export function useTermCardsByCommit(projectId: string | null, commits: readonly string[]): ReadonlyMap<string, TermCards | null> {
  const versions = [...new Set(commits)].join(" ");
  const key = `${projectId ?? ""} ${versions}`;
  const [loaded, setLoaded] = useState<{ readonly key: string; readonly cards: ReadonlyMap<string, TermCards | null> }>({ key: "", cards: new Map() });
  useEffect(() => {
    const [project = "", ...wanted] = key.split(" ");
    if (project === "") return;
    let current = true;
    const found = new Map<string, TermCards | null>();
    const settle = (commit: string, cards: TermCards | null) => {
      found.set(commit, cards);
      if (current) setLoaded({ key, cards: new Map(found) });
    };
    for (const commit of wanted.filter((each) => each !== "")) termCardsAt(project, commit).then((cards) => { settle(commit, cards); }, () => { settle(commit, null); });
    return () => { current = false; };
  }, [key]);
  const unreadable = useMemo(() => new Map(versions.split(" ").filter((each) => each !== "").map((commit) => [commit, null] as const)), [versions]);
  if (projectId === null) return unreadable;
  return loaded.key === key ? loaded.cards : NO_TERM_CARDS;
}

const NO_TERM_CARDS: ReadonlyMap<string, TermCards | null> = new Map();

/** A term's name: its label in the reader's language from the data model, else the end of its IRI. */
export function termName(iri: string, cards: TermCards | null, language: string): string {
  const labels = cards?.get(iri)?.labels ?? [];
  return orderedLabels(labels, language)[0]?.value ?? iriLocalName(iri);
}

/** A term's name at the model version `commit` (see `termName`). */
export function useTermName(iri: string, commit: string | null): string {
  const { i18n } = useTranslation();
  return termName(iri, useTermCards(commit), i18n.resolvedLanguage ?? "en");
}
